import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, theme, App as AntdApp } from 'antd';
import frFR from 'antd/locale/fr_FR';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./context/AuthContext";
import { WalletProvider } from "./context/WalletContext";
import { TradingProvider } from './context/TradingContext';
import { useAuth } from "./hooks/useAuth";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Trade from "./pages/Trade";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Packs from "./pages/Packs";
import MLM from "./pages/MLM";
import Legal from "./pages/Legal";
import Team from "./pages/Team";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import SpotTrading from "./pages/SpotTrading";
import FuturesTrading from "./pages/FuturesTrading";
import BonusPage from "./pages/BonusPage";
import AdminDashboard from "./pages/Admin/Dashboard";
import ManagerDashboard from "./pages/Manager/Dashboard";
import LeaderDashboard from "./pages/Leader/Dashboard";
import WalletDashboard from "./pages/WalletDashboard";
import MLMSettings from "./pages/Admin/MLMSettings";
import AdminUsers from "./pages/Admin/Users";
import InvestmentPlans from "./pages/Admin/InvestmentPlans";
import FinancialTransactions from './pages/Admin/FinancialTransactions';
import UserDetail from './pages/Admin/UserDetail';
import SystemSettings from './pages/Admin/SystemSettings';
import KYCManagement from './pages/Manager/KYCManagement';
import WithdrawalManagement from './pages/Manager/WithdrawalManagement';
import KYCDetail from './pages/Manager/KYCDetail';
import WithdrawalDetail from './pages/Manager/WithdrawalDetail';
import BonusManagement from './pages/Admin/BonusManagement';
import BrandingManagement from './pages/Admin/BrandingManagement';
import InvestmentTypes from './pages/Admin/InvestmentTypes';
import MarketAnalyses from './pages/Admin/MarketAnalyses';
import MarketControl from './pages/Admin/MarketControl';
import AdminKYCPage from './pages/Admin/KYCManagement';
import KYCVerification from './pages/KYCVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import GlobalStats from "./pages/Admin/GlobalStats";
import AuditLogs from "./pages/Admin/AuditLogs";
import Stats from "./pages/Stats";
import NotificationManagement from "./pages/Admin/NotificationManagement";
import MarketingManagement from "./pages/Admin/MarketingManagement";
import AdminCrm from "./pages/Admin/Crm";
import AdminCrmQuestions from "./pages/Admin/CrmQuestions";
import ManagerCrm from "./pages/Manager/Crm";
import MarketingLanding from "./pages/MarketingLanding";
import Support from "./pages/Support";
import SupportHub from "./pages/Admin/SupportHub.jsx";
import BottomNav from "./components/Layout/BottomNav";
const AdminLayoutWrapper = lazy(() => import("./components/Layout/AdminLayout"));

// Composant pour protéger les routes privées basé sur le rôle
function PrivateRoute({ children, requiredRole }) {
  const { isAuthenticated, loading, user } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950">
        <div className="text-slate-400 font-black uppercase tracking-[0.4em] text-xs">{t('common.syncing')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Vérification du rôle si spécifié
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin') {
    // Les admins peuvent accéder à toutes les pages
    if (user?.role !== 'admin') {
      return <Navigate to="/dashboard" />;
    }
  }

  return children;
}

// Composant pour les routes publiques (rediriger si déjà connecté)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-950">
        <div className="text-slate-400 font-black uppercase tracking-[0.4em] text-xs">{t('common.syncing')}</div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
}

// Routes de l'application
function AppRoutes() {
  return (
    <Routes>
      {/* Routes Publiques */}
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route 
        path="/packs" 
        element={
          <Packs />
        } 
      />
      <Route path="/mlm" element={<MLM />} />
      
      {/* Pages Légales */}
      <Route path="/security" element={<Legal />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/risk" element={<Legal />} />
      <Route path="/aml" element={<Legal />} />
      <Route path="/insurance" element={<Legal />} />
      <Route path="/audits" element={<Legal />} />
      
      {/* Routes d'authentification */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        } 
      />

      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        } 
      />

      <Route 
        path="/verify-email" 
        element={
          <PublicRoute>
            <VerifyEmail />
          </PublicRoute>
        } 
      />
      
      {/* Routes privées - Nécessitent une authentification */}

      <Route 
        path="/dashboard" 
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } 
      />

      <Route 
        path="/bonuses" 
        element={
          <PrivateRoute>
            <BonusPage />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/profile" 
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/settings" 
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/team" 
        element={
          <PrivateRoute>
            <Team />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/stats" 
        element={
          <PrivateRoute>
            <Stats />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/wallet" 
        element={
          <PrivateRoute>
            <WalletDashboard />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/trade" 
        element={<Navigate to="/spot" />} 
      />
      
      <Route 
        path="/spot" 
        element={
          <PrivateRoute>
            <SpotTrading />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/futures" 
        element={
          <PrivateRoute>
            <FuturesTrading />
          </PrivateRoute>
        } 
      />

      <Route 
        path="/support" 
        element={
          <PrivateRoute>
            <Support />
          </PrivateRoute>
        } 
      />
      
      {/* Redirection pour /trading */}
      <Route path="/trading" element={<Navigate to="/spot" />} />
      
      {/* Routes Admin */}
      <Route path="/admin" element={
          <PrivateRoute requiredRole="admin">
              <Suspense fallback={<div className="h-screen flex items-center justify-center bg-slate-950"><div className="text-slate-400 font-black uppercase tracking-[0.4em] text-xs">Chargement du Command Center...</div></div>}>
                  <AdminLayoutWrapper />
              </Suspense>
          </PrivateRoute>
      }>
          <Route index element={<AdminDashboard />} />
          <Route path="stats" element={<GlobalStats />} />
          <Route path="mlm" element={<MLMSettings />} />
          <Route path="audit-logs" element={<AuditLogs />} />
          <Route path="settings" element={<SystemSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<UserDetail />} />
          <Route path="invest" element={<InvestmentPlans />} />
          <Route path="invest-types" element={<InvestmentTypes />} />
          <Route path="market-analyses" element={<MarketAnalyses />} />
          <Route path="market-control" element={<MarketControl />} />
          <Route path="financial-transactions" element={<FinancialTransactions />} />
          <Route path="bonuses" element={<BonusManagement />} />
          <Route path="branding" element={<BrandingManagement />} />
          <Route path="kyc" element={<AdminKYCPage />} />
          <Route path="notifications" element={<NotificationManagement />} />
          <Route path="marketing" element={<MarketingManagement />} />
          <Route path="support" element={<SupportHub />} />
          <Route path="crm" element={<AdminCrm />} />
          <Route path="crm/questions" element={<AdminCrmQuestions />} />
      </Route>


      {/* User KYC Verification Page */}
      <Route 
        path="/kyc" 
        element={
          <PrivateRoute>
            <KYCVerification />
          </PrivateRoute>
        } 
      />

      {/* Routes Manager */}
      <Route 
        path="/manager" 
        element={
          <PrivateRoute requiredRole="manager">
            <ManagerDashboard />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/manager/kyc" 
        element={
          <PrivateRoute requiredRole="manager">
            <KYCManagement />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/manager/kyc/:id" 
        element={
          <PrivateRoute requiredRole="manager">
            <KYCDetail />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/manager/withdrawals" 
        element={
          <PrivateRoute requiredRole="manager">
            <WithdrawalManagement />
          </PrivateRoute>
        } 
      />
      
      <Route 
        path="/manager/withdrawals/:id" 
        element={
          <PrivateRoute requiredRole="manager">
            <WithdrawalDetail />
          </PrivateRoute>
        } 
      />

      <Route 
        path="/manager/crm" 
        element={
          <PrivateRoute requiredRole="manager">
            <ManagerCrm />
          </PrivateRoute>
        } 
      />

      {/* Marketing Landing Page */}
      <Route 
        path="/formations" 
        element={<MarketingLanding />} 
      />

      <Route 
        path="/marketing" 
        element={<MarketingLanding />} 
      />
      
      {/* Routes Leader */}
      <Route 
        path="/leader" 
        element={
          <PrivateRoute requiredRole="leader">
            <LeaderDashboard />
          </PrivateRoute>
        } 
      />
      
      {/* Route par défaut - Redirection */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

// App principal avec AuthProvider
function App() {
  const { i18n } = useTranslation();
  
  const getAntdLocale = () => {
    switch(i18n.language) {
      case 'en': return enUS;
      case 'es': return esES;
      default: return frFR;
    }
  };

  return (
    <ConfigProvider
      locale={getAntdLocale()}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#6366f1',
          colorBgBase: '#0f172a',
          colorBgContainer: '#1e293b',
          colorBgElevated: '#1e293b',
          colorBorder: 'rgba(255,255,255,0.1)',
          colorText: '#e2e8f0',
          colorTextSecondary: '#94a3b8',
          colorTextHeading: '#f1f5f9',
          borderRadius: 8,
          fontFamily: "'Inter', -apple-system, sans-serif",
        },
        components: {
          Table: {
            colorBgContainer: '#1e293b',
            headerBg: '#0f172a',
            rowHoverBg: 'rgba(99,102,241,0.08)',
            borderColor: 'rgba(255,255,255,0.06)',
          },
          Card: {
            colorBgContainer: '#1e293b',
            colorBorderSecondary: 'rgba(255,255,255,0.06)',
          },
          Tabs: {
            colorBgContainer: '#1e293b',
            itemColor: '#94a3b8',
            itemSelectedColor: '#6366f1',
            itemHoverColor: '#e2e8f0',
            inkBarColor: '#6366f1',
          },
          Modal: {
            colorBgElevated: '#1e293b',
          },
          Select: {
            colorBgContainer: '#0f172a',
            colorBgElevated: '#1e293b',
          },
          Input: {
            colorBgContainer: '#0f172a',
          },
          Button: {
            colorPrimary: '#6366f1',
            colorPrimaryHover: '#4f46e5',
          },
        }
      }}
    >
      <AntdApp>
        <Router>
          <AuthProvider>
            <WalletProvider>
              <TradingProvider>
                <AppRoutes />
                <BottomNav />
              </TradingProvider>
            </WalletProvider>
          </AuthProvider>
        </Router>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;